// history: the native history api sometimes doesnt work as expected, for example:
// replacestate adds a new entry in firefox, so sometimes
// safari sometimes randomly forgets a history entry
// one user had this issue: when opening a project overlay, then going back, it would open a youtube or vimeo url :D
// instagram browser does not work: back and forth buttons do nothing, so i had to do hard refreshes instead
// sometimes if i open a project overlay, i have to click back twice to go back to the previous page

// that is why i now use https://www.npmjs.com/package/history

import layout_controller from '../layout/layout_controller'
import shortcodes_controller from '../shortcodes/shortcodes_controller'
import { canUseAjaxNavigation } from './navigation_policy'
import hard_navigation_transition from './hard_navigation_transition'
import { bindAnchors } from './bind_anchors'
import prevnext_controller from '../prevnext/prevnext_controller'
import globalevents_controller from '../globalevents/globalevents_controller'
import { Utility, utility_controller } from '../utility/utility_controller'
import { getMasonryColumns, waitForMasonryInitialized } from '../utility/utility_controller'
import { transition_timer_controller } from '../history/transition_timer'
import { activateCurrentMenuPoint } from './activate_menupoints'
import animations_controller from '../animations/animations_controller'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'
import { NAVIGATION_ANIMATION_PHASES } from '../animations/lifecycle/animation_lifecycle_events'
import { getNavigationScopeForType } from '../scope/scope'
import { hideAllOverlays } from '../page_overlay/page_overlay'
import projects_overlays from '../projects_overlays/projects_overlays'
import visibility_gate from '../animations/lifecycle/visibility_gate'
import { SCOPES } from '../scope/scope'
import { createBrowserHistory } from 'history'

// Modern history package from Remix team - handles Safari/mobile browser quirks
// https://github.com/remix-run/history
const history = createBrowserHistory()

// Expose globally for other modules to access
window.layHistory = history

// ============================================================================
// CRITICAL: Block external history manipulation from iframes
// ============================================================================
// Problem: Vimeo and YouTube iframes can add their own history entries when they load.
// This causes the "double back button" issue:
//   1. User is on main page
//   2. User clicks to open project overlay (with Vimeo iframe inside)
//   3. Vimeo iframe loads and secretly adds a history entry
//   4. Now there are 2 entries: one from our pushState, one from Vimeo
//   5. User clicks back once → goes to Vimeo's entry (still on project page!)
//   6. User clicks back again → finally goes back to main page
//
// Solution: Intercept ALL native window.history.pushState/replaceState calls
// and ONLY allow calls that come from our controlled history package.
// This blocks Vimeo/YouTube/any external scripts from manipulating history.
// ============================================================================

var nativePushState = window.history.pushState
var nativeReplaceState = window.history.replaceState

// Flag to allow calls from the modern history package
var allowHistoryChange = false
var skipNextTransitionOut = false

// Override native pushState to block external calls
window.history.pushState = function () {
	if (!allowHistoryChange) {
		// Block the call - external source trying to manipulate history
		return
	}
	var result = nativePushState.apply(window.history, arguments)
	allowHistoryChange = false // Reset flag
	return result
}

// Override native replaceState to block external calls
window.history.replaceState = function () {
	if (!allowHistoryChange) {
		// Block the call - external source trying to manipulate history
		return
	}
	var result = nativeReplaceState.apply(window.history, arguments)
	allowHistoryChange = false // Reset flag
	return result
}

// Wrap the modern history package methods to set the allow flag
// This ensures OUR code can still call pushState/replaceState
var originalHistoryPush = history.push
var originalHistoryReplace = history.replace

history.push = function () {
	allowHistoryChange = true // Allow the next native call
	return originalHistoryPush.apply(history, arguments)
}

history.replace = function () {
	allowHistoryChange = true // Allow the next native call
	return originalHistoryReplace.apply(history, arguments)
}

// `transition_in` selects which cols animate via `getColumnsToAnimate`, which
// reads viewport position from `getBoundingClientRect`. When restoring scroll
// after a popstate, the scroll target lives in #lay-project-overlay (or window)
// and is applied asynchronously — after masonry init, after a 100ms wait for
// thumbnailgrid, etc. If `transition_in` runs first, the wrong cols are picked
// and elements at the restored scroll position pop in without staggering. This
// Promise lets `transition_in` await scroll restoration explicitly.
var scrollRestoreReady = Promise.resolve()
var resolveScrollRestoreReady = null
var beginScrollRestoreWait = function () {
	scrollRestoreReady = new Promise(function (resolve) {
		resolveScrollRestoreReady = resolve
	})
}
var endScrollRestoreWait = function () {
	if (resolveScrollRestoreReady) {
		var r = resolveScrollRestoreReady
		resolveScrollRestoreReady = null
		r()
	}
}
var getScrollRestoreReady = function () {
	return scrollRestoreReady
}

var currentScrollPos = 0
var lastScrollPos = 0

var lastFooterScrollPos = 0

var currentFpSlide = 0
var lastFpSlide = 0
var fpSlidePosOfPageBeforeOverlay = 0
var pageBeforeProjectOverlayHadFullscreenSlider = false

var jqueryMap = {
	$body: null,
}

var mainPageHasFullscreenSlider = function () {
	var sliderSelector = '.vertical-fullpage, .horizontal-fullpage, .vertical-scrollsnap, .horizontal-scrollsnap'
	var $mainContent = jQuery('body > .lay-content')
	return $mainContent.filter(sliderSelector).add($mainContent.find(sliderSelector)).length > 0
}

var getState = function () {
	// Get state from history location
	return history.location.state
}

var initHistory = function () {
	// prevent jumping of page when going back and forth
	if ('scrollRestoration' in window.history) {
		window.history.scrollRestoration = 'manual'
	}

	// Only set initial state if there isn't one already
	// This prevents adding an extra history entry on page load
	// (which can happen with embedded iframes like Vimeo)
	if (!history.location.state) {
		var obj = {
			type: jqueryMap.$body.attr('data-type'),
			id: jqueryMap.$body.attr('data-id'),
			catid: jqueryMap.$body.attr('data-catid'), // Store as string, not array
			slug: jqueryMap.$body.attr('data-slug'),
			title: document.title,
		}
		if (window.layCachePreload && typeof window.layCachePreload.rememberQTranslateLang === 'function') {
			obj.qtranslate_lang = window.layCachePreload.rememberQTranslateLang(window.location.href)
		}
		// Use pathname || '/' so myurl.com#hello works like myurl.com/#hello
		history.replace((window.location.pathname || '/') + window.location.search + window.location.hash, obj)
	}
	projects_overlays.setFirstState()
}

function decodeHtmlCharCodes(str) {
	if (str === null || str === undefined) {
		return str
	}
	return str.replace(/(&#(\d+);)/g, function (match, capture, charCode) {
		return String.fromCharCode(charCode)
	})
}

var pushState = function (stateObj, title, href, options) {
	if (layData.navigation_policy && layData.navigation_policy.enabled && !canUseAjaxNavigation(stateObj, href)) {
		if (!hard_navigation_transition.navigate(href, stateObj, null)) {
			window.location.href = href
		}
		return
	}
	if (isSameState(stateObj, href)) {
		skipNextTransitionOut = false
		return
	}
	if (
		stateObj &&
		!stateObj.qtranslate_lang &&
		window.layCachePreload &&
		typeof window.layCachePreload.getQTranslateLangForNavigation === 'function'
	) {
		stateObj = Object.assign({}, stateObj, {
			qtranslate_lang: window.layCachePreload.getQTranslateLangForNavigation(href),
		})
	}
	projects_overlays.setShouldOpenAndShouldCloseOnPush(stateObj)
	bindScrollToTop()
	// Use history.push() for better cross-browser support (Safari, mobile)
	// Extract path from full URL; use pathname || '/' so myurl.com#hello works like myurl.com/#hello
	var urlObj = new URL(href)
	// When navigating to another page with a hash, push path+search only and store hash in state.
	// After content loads, we replace the entry with path+hash so one Back returns to the previous page.
	var pathAndSearch = (urlObj.pathname || '/') + urlObj.search
	if (urlObj.hash) {
		stateObj = Object.assign({}, stateObj, { initialHash: urlObj.hash })
		history.push(pathAndSearch, stateObj)
	} else {
		history.push(pathAndSearch, stateObj)
	}
	// Skip updating document.title when options.skipTitleUpdate (e.g. AJAX link) so we don't flash wrong title; layout response will set correct Yoast title
	if (!options || !options.skipTitleUpdate) {
		document.title = decodeHtmlCharCodes(title)
	}
	globalevents_controller.triggerPushState()
	if (projects_overlays.shouldCloseOverlay()) {
		if (projects_overlays.isSameStateOfLastOverview(stateObj)) {
			// Emit event BEFORE starting animation so other modules can prepare
			window.laytheme.emit('overlaystartsanimatingout')

			// instantly animateOut if we just go back to the last overview page.
			// otherwise, just animateOut, once the new page is loaded in background underneath the overlays
			projects_overlays.removeCloseIcon()
			projects_overlays.animateOut(function () {
				projects_overlays.destroyOverlay()
			})
		}
		handleStateChangeCloseProjectOverlay(stateObj)
	} else {
		handleStateChange(stateObj)
	}
}

var isSameState = function (stateObj, newHref) {
	if (window.location.href != newHref) {
		return false
	}
	var currentState = history.location.state
	if (currentState == null) {
		return false
	}
	if (currentState.id == stateObj.id && currentState.type == stateObj.type) {
		return true
	}
	return false
}

var replaceState = function (hash) {
	// Create a new history entry with updated hash, keeping the same state object
	// This is used when clicking anchor links on the same page
	// This allows the back button to work properly (going back through anchor history)
	var currentState = history.location.state
	var currentUrl = new URL(window.location.href)
	var newUrl = (currentUrl.pathname || '/') + currentUrl.search + hash
	if (currentState) {
		// Use history.push() to create a new entry with the state object
		// This ensures back button works correctly
		// Note: history.push() updates the URL including the hash, so we don't need to set window.location.hash
		history.push(newUrl, currentState)
	} else {
		// Fallback: direct loads may not have a history state yet. Still use
		// pushState instead of window.location.hash so the browser does not
		// perform its own native anchor jump after Lay's animated scroll.
		history.push(newUrl, null)
	}
}

// Replace the current history entry with an updated hash. Project overlays use
// this variant so their close action still returns directly to the underlay
// instead of stopping at an extra same-project anchor history entry first.
var replaceCurrentStateHash = function (hash) {
	var currentState = history.location.state
	var currentUrl = new URL(window.location.href)
	var newUrl = (currentUrl.pathname || '/') + currentUrl.search + hash
	allowHistoryChange = true
	history.replace(newUrl, currentState)
	allowHistoryChange = false
}

// Replace current history entry to add the hash (from initialHash in state). Used after navigating to another page with an anchor so one Back returns to the previous page.
var applyInitialHashAndReplace = function () {
	var loc = history.location
	var state = loc.state
	if (!state || !state.initialHash) return
	var path = (loc.pathname || '/') + (loc.search || '') + state.initialHash
	var newState = Object.assign({}, state)
	delete newState.initialHash
	allowHistoryChange = true
	history.replace(path, newState)
	allowHistoryChange = false
}

var consumeInitialHashWithoutReplace = function () {
	var loc = history.location
	var state = loc.state
	if (!state || !state.initialHash) return ''
	var initialHash = state.initialHash
	var path = (loc.pathname || '/') + (loc.search || '') + (loc.hash || '')
	var newState = Object.assign({}, state)
	delete newState.initialHash
	allowHistoryChange = true
	history.replace(path, newState)
	allowHistoryChange = false
	return initialHash
}

// Remove a hash without creating another browser-history entry. This is used
// after a direct page load has already scrolled to its anchor and the
// "Do not add anchor hash to URL" option is enabled.
var removeCurrentHash = function () {
	var loc = history.location
	if (!loc.hash) return
	var path = (loc.pathname || '/') + (loc.search || '')
	allowHistoryChange = true
	history.replace(path, loc.state)
	allowHistoryChange = false
}

// Update a single search param on the current URL via the safe history
// API (so the native pushState/replaceState override doesn't block it).
// Used by the search overlay to write `?lay-search=…` on Enter so the
// query is shareable. Pass an empty / falsy value to remove the param.
// State object is preserved.
var updateUrlSearchParam = function (name, value) {
	if (typeof URLSearchParams !== 'function') return
	var loc = history.location
	var pathname = loc.pathname || '/'
	var searchParams = new URLSearchParams(loc.search || '')
	if (value && String(value).length > 0) {
		searchParams.set(name, String(value))
	} else {
		searchParams.delete(name)
	}
	var newSearch = searchParams.toString()
	var newPath = pathname + (newSearch ? '?' + newSearch : '') + (loc.hash || '')
	allowHistoryChange = true
	history.replace(newPath, loc.state)
	allowHistoryChange = false
}

// Update the current history entry's title (and document.title). Used after AJAX layout load so Yoast SEO title is applied.
var updateCurrentStateTitle = function (title) {
	if (title == null || title === '') return
	var loc = history.location
	var state = loc.state
	var decoded = decodeHtmlCharCodes(title)
	if (!state) {
		document.title = decoded
		return
	}
	var path = (loc.pathname || '/') + (loc.search || '') + (loc.hash || '')
	var newState = Object.assign({}, state, { title: title })
	document.title = decoded
	allowHistoryChange = true
	history.replace(path, newState)
	allowHistoryChange = false
}

var bindPopState = function () {
	// Use history.listen() from modern history package
	// This provides consistent behavior across all browsers including Safari/mobile
	history.listen(({ location, action }) => {
		var stateObj = location.state

		// IMPORTANT: history.push() triggers this listener immediately!
		// We need to ignore PUSH and REPLACE actions since pushState() already handles them
		if (action === 'PUSH' || action === 'REPLACE') {
			return
		}

		if (window.layCachePreload && typeof window.layCachePreload.rememberQTranslateLangFromHistoryState === 'function') {
			window.layCachePreload.rememberQTranslateLangFromHistoryState()
		}

		// if we press back and forth really quickly, we might still be doing "layout_controller.showLayout" in the background called in "handleStateChange"
		// this can lead to the page we came from being replaced with a project!
		// so we abort the fetch here
		layout_controller.abortFetch()

		projects_overlays.setShouldOpenAndShouldCloseOnPop(stateObj)

		// Update document title when navigating with browser back/forward buttons
		if (stateObj && stateObj.title) {
			document.title = decodeHtmlCharCodes(stateObj.title)
		}

		window.laytheme.emit('popstate')
		if (stateObj && stateObj.type == jQuery('body').attr('data-type') && stateObj.id == jQuery('body').attr('data-id')) {
			// same page but state change was fired!
			// probably just a hashchange was fired by going back or forth using the browser buttons
			// no need to reload page because were still on the same page
			// but we need to trigger anchor scroll if hash changed
			// Use setTimeout to ensure the hashchange happens after the URL has updated
			setTimeout(function () {
				if (location.hash) {
					// Update window.location.hash to match history location, then trigger hashchange
					window.location.hash = location.hash
					// Trigger hashchange event so anchor behavior can handle it
					jQuery(window).trigger('hashchange')
				}
			}, 0)
		} else if (stateObj) {
			// Handle state change (including projects overlay)
			if (projects_overlays.shouldCloseOverlay()) {
				// Emit event BEFORE starting animation so other modules can prepare
				window.laytheme.emit('overlaystartsanimatingout')

				projects_overlays.animateOut(function () {
					projects_overlays.destroyOverlay()
				})

				projects_overlays.removeCloseIcon()
				handleStateChangeCloseProjectOverlay(stateObj)
			} else {
				// this is just the behaviour for a normal link or popstate
				bindScrollToLastPosition(stateObj)
				handleStateChange(stateObj)
			}
		} else {
			// No stateObj - this can happen in edge cases or when navigating to pages without state
			// If the pathname changed, we need to reload the page
			var currentPathname = window.location.pathname
			var newPathname = location.pathname
			if (currentPathname !== newPathname) {
				// Pathname changed but no state object - do a full page reload
				// Use pathname || '/' so myurl.com#hello works like myurl.com/#hello
				window.location.href = (location.pathname || '/') + location.search + location.hash
			} else {
				// Same pathname, just hash changed - trigger hashchange
				setTimeout(function () {
					if (location.hash) {
						window.location.hash = location.hash
						jQuery(window).trigger('hashchange')
					}
				}, 0)
			}
		}
	})
}

var setFooterScrollTop = function () {
	// for splitscreen
	if (document.querySelector('#footer-region') != null) {
		lastFooterScrollPos = document.querySelector('#footer-region').scrollTop
	}
}

var handleStateChangeCloseProjectOverlay = function (stateObj) {
	// so if i just close the overlay to go back to the last overview page, i literally just close the div:
	if (projects_overlays.isSameStateOfLastOverview(stateObj)) {
		// todo: does that make sense?
		if (jQuery('.post-password-form').length > 0) {
			window.location.reload()
			return
		}

		// Convert catid from string to array for setBodyClasses
		var stateObjWithArrayCatid = Object.assign({}, stateObj, {
			catid: utility_controller.parseIdsString(stateObj.catid),
		})
		utility_controller.setBodyClasses(stateObjWithArrayCatid)

		// Update edit button URL
		updateEditButtonUrl(stateObj.type, stateObj.id)

		activateCurrentMenuPoint()
		window.laytheme.emit('newpage')
		globalevents_controller.triggerNewPageShown({}, stateObj.type, stateObj, { isProjectOverlay: false })

		// here, go back to last fullscreen slider slide:
		scrollToLastPos()
	} else {
		// but if i close the overlay to go to a different page, i need to do the whole thing:
		handleStateChange(stateObj)
	}
}

var handleStateChange = function (stateObj) {
	if (jQuery('.post-password-form').length > 0) {
		window.location.reload()
		return
	}

	var shouldSkipTransitionOut = skipNextTransitionOut
	skipNextTransitionOut = false
	transition_timer_controller.startTimer()
	if (shouldSkipTransitionOut) {
		if (typeof animations_controller.skipNextTransitionOut == 'function') {
			animations_controller.skipNextTransitionOut()
		}
	}
	lastScrollPos = currentScrollPos
	if (projects_overlays.isOverlayDisplayBlock()) {
		currentScrollPos = document.querySelector('#lay-project-overlay').scrollTop
	} else {
		currentScrollPos = window.scrollY
	}

	lastFpSlide = currentFpSlide
	currentFpSlide = currentSlidePos

	var doreload = shortcodes_controller.pageContainsShortcode(stateObj)
	if (!canUseAjaxNavigation(stateObj, window.location.href)) {
		doreload = true
	}

	if (!doreload) {
		hideAllOverlays()
		// when duration is 0, or transition in and out are none, dont hide old content, just replace content
		// this way we dont have a white screen in between
		var hideRegions = false
		if (parseInt(layData.navigation_transition_out_duration, 10) != 0) {
			hideRegions = true
		}
		// duration can be bigger than 0, but we may still want to not do hideRegions, if transition out is set to none!
		if (layData.navigation_transition_out == 'none') {
			hideRegions = false
		}
		// if projects_overlays feature is active, it needs to show to overlay and keep the old content shown
		if (window.utility_controller.isProjectsOverlayEnabled()) {
			hideRegions = true
			if (projects_overlays.shouldCloseOverlay()) {
				hideRegions = false
			}
			if (projects_overlays.shouldOpenOverlay()) {
				// if we're about to open a project overlay, we remember the fullscreen slider position we are at right now
				fpSlidePosOfPageBeforeOverlay = currentSlidePos
				pageBeforeProjectOverlayHadFullscreenSlider = mainPageHasFullscreenSlider()
				hideRegions = false
			}
		}

		if (hideRegions) {
			// basically this just starts the "transition out"
			layout_controller.hideRegions()
			if (shouldSkipTransitionOut && typeof transition_timer_controller.completeTimer == 'function') {
				transition_timer_controller.completeTimer()
			}
		} else {
			// if we dont start transition out, we just like say it has ended
			// i think we need this for the layout controller
			animations_controller.setTransitionStatus('ended')
			animation_lifecycle_controller.setNavigationPhase(NAVIGATION_ANIMATION_PHASES.OUT_ENDED, {
				scope: getNavigationScopeForType(projects_overlays.isOverlayDisplayBlock() ? 'project_overlay' : 'normal'),
			})
			window.laytheme.emit('transition_navigation_out_completed')
		}

		// do the following instead via the values I get from the returned php
		jQuery('body').attr({
			'data-type': stateObj.type,
			'data-id': stateObj.id,
			'data-catid': stateObj.catid, // Already a string, no conversion needed
		})

		// Update edit button URL
		updateEditButtonUrl(stateObj.type, stateObj.id)

		// A project overlay is still part of the overview the visitor opened it from.
		// Keep that overview's active menu classes while opening the overlay and while
		// navigating between projects inside it. Closing the overlay activates the
		// destination menu point in handleStateChangeCloseProjectOverlay instead.
		var preserveOverviewMenuPoint =
			projects_overlays.shouldOpenOverlay() ||
			(projects_overlays.isOverlayDisplayBlock() && !projects_overlays.shouldCloseOverlay())
		if (!preserveOverviewMenuPoint) {
			activateCurrentMenuPoint()
		}
		// set scroll top shortly before content is replaced
		// Add the loading indicator + start the open animation BEFORE
		// `showLayout()`. `showLayout()` runs `afterSuccess()` synchronously
		// when the navigation is served from the master/preload cache, which
		// means `replaceContentAndFireEvents()` (and the
		// `removeLoadingIndicator()` we put at the top of it) would run
		// before this addLoadingIndicator if we kept the old order — leaving
		// the `po-content-loading` class stuck on `#lay-project-overlay`
		// permanently. Adding first guarantees add-then-remove ordering in
		// both cache-hit (sync) and cache-miss (async fetch) paths.
		if (projects_overlays.shouldOpenOverlay()) {
			// Insert the project-overlay hide-css NOW, at click time, rather than
			// waiting for `before_content_replacement_for_project_overlay` to fire
			// inside replaceContentAndFireEvents. The cached-content path can run
			// content insertion in the very same JS tick as the click, and any
			// extra work in between (animations, listeners, async paint timing)
			// has caused a flash where the new overlay content briefly renders
			// before its `opacity:0` hide rule lands. The gate's hideScope
			// internally cancels any pending safety-reveal from a prior
			// transition, so the 3s safety setTimeout scheduled at the end of
			// the last `doTransitionIn` cannot race ahead and rip our fresh
			// hide-css off mid-flight.
			visibility_gate.hideScope(SCOPES.PROJECT_OVERLAY)
			if (layData.po_opening_behaviour == 'immediate') {
				projects_overlays.animateIn()
				projects_overlays.addLoadingIndicator()
			}
		}

		// this way ppl can scroll while content being loaded and it will still work
		layout_controller.showLayout('', setFooterScrollTop)
		// Used in search_view and elsewhere and could be used by clients in their custom js. 'newpage' event is triggered before a new page is fetched. basically it is just triggered when history changes
		// probably this event should be called something else than 'newpage' but i will keep using this name for backwards compatibility
		window.laytheme.emit('newpage')
	} else {
		window.location.reload(false)
	}
}

// what im doing here is, when someone clicks the "back" button in the browser, scroll to the position
// where they left off
var bindScrollToLastPosition = function (state) {
	beginScrollRestoreWait()
	window.laytheme.off('newpageshown', scrollToTop)
	window.laytheme.on('newpageshown', scrollToLastPositionHelper)
}
var scrollToLastPositionHelper = function () {
	// there are two cases where we have a hash in a URL
	// 1: a scroll to
	// 2: a category filter
	// if its a category filter scroll to lastScrollPos, otherwise don't, because my anchorscroll js will handle this

	// if there is either no # and there is no hash of #category- (used for thumbnailgrid filtering)
	var do_scrollToLastPos = !urlContainsScrollableHash()

	if (layData.never_change_scrolling_position == 'on' && Utility.screenSize != 'phone') {
		do_scrollToLastPos = false
	}

	if (layData.misc_options_disable_ajax == 'on') {
		do_scrollToLastPos = false
	}

	if (do_scrollToLastPos) {
		scrollToLastPos()
	} else {
		endScrollRestoreWait()
	}

	// unbind itself
	window.laytheme.off('newpageshown', scrollToLastPositionHelper)
}

var restoreMainPageScrollAfterProjectOverlayClose = function () {
	var scrollPos = currentScrollPos
	var restore = function () {
		window.scrollTo(0, scrollPos)
		document.documentElement.scrollTop = scrollPos
		jQuery(document).scrollTop(scrollPos)
		endScrollRestoreWait()
	}
	var restoreAfterDestroy = function () {
		window.laytheme.off('destroyoverlay', restoreAfterDestroy)
		if (typeof requestAnimationFrame == 'function') {
			requestAnimationFrame(restore)
		} else {
			setTimeout(restore, 0)
		}
	}
	if (projects_overlays.isOverlayDisplayBlock()) {
		window.laytheme.off('destroyoverlay', restoreAfterDestroy)
		window.laytheme.on('destroyoverlay', restoreAfterDestroy)
	} else {
		restore()
	}
}

var scrollToLastPos = function () {
	// Don't restore scroll position when opening a project overlay - always start at position 0
	var currentState = history.location.state
	if (projects_overlays.isOverlayDisplayBlock() && !projects_overlays.isSameStateOfLastOverview(currentState)) {
		endScrollRestoreWait()
		return
	}

	// when coming back from a project overlay, and the page we came from has a fullscreen slider, we need to scroll to the last slide
	// when opening a project overlay, the fullscreen slider on the page we came from is destroyed
	// fullscreen slider is re-initialized after the overlay is closed, so we need to wait for the re-initialization:
	if (projects_overlays.isSameStateOfLastOverview(currentState)) {
		if (!pageBeforeProjectOverlayHadFullscreenSlider) {
			restoreMainPageScrollAfterProjectOverlayClose()
			return
		}

		// when coming back from a project overlay to the underlying page,
		// i only need to set the scroll position of the fullscreen slider
		// i dont need to set the normal scroll position i think, because it never changed
		// because in the project overlay, we dont scroll the body, we scroll the div containg the project overlay!

		// For CSS Scroll Snap vs fullpage.js: different restoration strategies
		var isScrollSnapType = typeof magneticSlidesPassedData !== 'undefined' && magneticSlidesPassedData.ms_slide_type === 'css_scroll_snap'

		if (isScrollSnapType) {
			// CSS Scroll Snap: main page instance is still active (not destroyed)
			// Wait for overlay to be fully hidden before restoring position
			setTimeout(function () {
				if (typeof jQuery.fn.fullpage.silentMoveTo == 'function') {
					if (magneticSlidesPassedData.ms_direction == 'horizontal') {
						jQuery.fn.fullpage.silentMoveTo(1, fpSlidePosOfPageBeforeOverlay)
					} else {
						jQuery.fn.fullpage.silentMoveTo(fpSlidePosOfPageBeforeOverlay, 0)
					}
				}
				endScrollRestoreWait()
			}, 300)
		} else {
			// fullpage.js: need to restore position after re-initialization
			jQuery(window).off('fpAfterRender')
			jQuery(window).on('fpAfterRender', function () {
				if (typeof jQuery.fn.fullpage.silentMoveTo == 'function') {
					if (magneticSlidesPassedData.ms_direction == 'horizontal') {
						jQuery.fn.fullpage.silentMoveTo(1, fpSlidePosOfPageBeforeOverlay)
					} else {
						jQuery.fn.fullpage.silentMoveTo(fpSlidePosOfPageBeforeOverlay, 0)
					}
				}
				endScrollRestoreWait()
			})
		}
	} else {
		// or scroll to slide if fullpage slider is active
		var isScrollSnapEnabled = jQuery('html').hasClass('scrollsnap-enabled')
		var isFullPageEnabled = jQuery('html').hasClass('fp-enabled')

		if ((isFullPageEnabled || isScrollSnapEnabled) && typeof jQuery.fn.fullpage != 'undefined' && typeof magneticSlidesPassedData != 'undefined' && typeof jQuery.fn.fullpage.silentMoveTo == 'function') {
			if (magneticSlidesPassedData.ms_direction == 'horizontal') {
				jQuery.fn.fullpage.silentMoveTo(1, lastFpSlide)
			} else {
				jQuery.fn.fullpage.silentMoveTo(lastFpSlide, 0)
			}
			endScrollRestoreWait()
		} else {
			// wait a little because a masonry markup might need to build which changes the document height
			var $parents = jQuery(parent_selectors.join(','))
			var masonryCols = getMasonryColumns($parents)

			if (masonryCols.length > 0) {
				jQuery
					.when(waitForMasonryInitialized(masonryCols))
					.then(function () {
						if (projects_overlays.isOverlayDisplayBlock()) {
							// only scroll to last pos if we go between projects while overlay is open:
							if (projects_overlays.shouldCloseOverlay() == false && projects_overlays.shouldOpenOverlay() == false) {
								document.querySelector('#lay-project-overlay').scrollTo(0, lastScrollPos)
							}
						} else {
							window.scrollTo(0, lastScrollPos)
						}
						endScrollRestoreWait()
					})
					.catch(function (err) {
						endScrollRestoreWait()
					})
			} else {
				if ($parents.find('.type-thumbnailgrid').length > 0) {
					// seems like yes we need to wait a little for thumbnailgrid to build, even if its not a masonry grid
					setTimeout(function () {
						if (projects_overlays.isOverlayDisplayBlock()) {
							// only scroll to last pos if we go between projects while overlay is open:
							if (projects_overlays.shouldCloseOverlay() == false && projects_overlays.shouldOpenOverlay() == false) {
								document.querySelector('#lay-project-overlay').scrollTo(0, lastScrollPos)
							}
						} else {
							window.scrollTo(0, lastScrollPos)
						}
						endScrollRestoreWait()
					}, 100)
				} else {
					if (projects_overlays.isOverlayDisplayBlock()) {
						if (projects_overlays.shouldCloseOverlay() == false && projects_overlays.shouldOpenOverlay() == false) {
							document.querySelector('#lay-project-overlay').scrollTo(0, lastScrollPos)
						}
					} else {
						window.scrollTo(0, lastScrollPos)
					}
					endScrollRestoreWait()
				}
			}
		}
	}
}

// here i just scroll to top, when someone just clicked on a link, no going back, using the back button
var bindScrollToTop = function () {
	// If a previous popstate set up a pending scroll-restore but the user
	// navigated forward before it fired, clear the pending Promise so
	// transition_in isn't stuck awaiting it.
	endScrollRestoreWait()
	window.laytheme.off('newpageshown', scrollToLastPositionHelper)
	window.laytheme.on('newpageshown', scrollToTop)
}

// A site's custom CSS may set `scroll-behavior: smooth` on html/body. Native
// window.scrollTo(0, 0) then starts an animation that can race with AJAX
// content replacement and leave the newly shown page at the old scroll
// position. Force this navigation reset to be synchronous while preserving
// the site's inline styles for all intentional scrolling afterwards.
var scrollWindowToTopInstantly = function () {
	var scrollElements = [document.documentElement, document.body].filter(Boolean)
	var previousScrollBehaviors = scrollElements.map(function (element) {
		return {
			element: element,
			value: element.style.getPropertyValue('scroll-behavior'),
			priority: element.style.getPropertyPriority('scroll-behavior'),
		}
	})

	previousScrollBehaviors.forEach(function (state) {
		state.element.style.setProperty('scroll-behavior', 'auto', 'important')
	})

	if (window.smooth_scroll_controller && typeof window.smooth_scroll_controller.scrollTo === 'function') {
		window.smooth_scroll_controller.scrollTo(0, { immediate: true, force: true })
	}

	try {
		window.scrollTo({ left: 0, top: 0, behavior: 'instant' })
	} catch (error) {
		window.scrollTo(0, 0)
	}

	// Cover older engines and custom scrolling code that reads either root.
	document.documentElement.scrollTop = 0
	if (document.body) {
		document.body.scrollTop = 0
	}

	previousScrollBehaviors.forEach(function (state) {
		if (state.value === '') {
			state.element.style.removeProperty('scroll-behavior')
		} else {
			state.element.style.setProperty('scroll-behavior', state.value, state.priority)
		}
	})
}

var scrollToTop = function () {
	if (layData.never_change_scrolling_position == 'on' && Utility.screenSize != 'phone') {
		// "dont scroll"
		// actually this is for people who use the splitscreen feature
		// we scroll the footer region to its previous scroll position so it looks like it hasn't scrolled
		if (document.querySelector('#footer-region') != null) {
			document.querySelector('#footer-region').scrollTo(0, lastFooterScrollPos)
		}
	} else {
		if (!projects_overlays.shouldCloseOverlay() && projects_overlays.isOverlayDisplayBlock()) {
			// here, we are just going from one page to the next while the project overlay is open
			if (!urlContainsScrollableHash() && layData.misc_options_disable_ajax != 'on') {
				if (layData.po_animation == 'none') {
					// seems like in this case i need to do a timeout to make scroll up work :D
					setTimeout(function () {
						jQuery('#lay-project-overlay').scrollTop(0)
					}, 0)
				} else {
					if (!projects_overlays.shouldCloseOverlay() && !projects_overlays.shouldOpenOverlay()) {
						// when we just go from one project to another project, while the overlay is open!
						jQuery('#lay-project-overlay').scrollTop(0)
					}
				}
			}
		} else {
			if (window.utility_controller.isProjectsOverlayEnabled()) {
				var currentState = history.location.state
				// only scroll to top if page we're going to is not the current overview page that we're coming from :D
				if (!projects_overlays.isSameStateOfLastOverview(currentState)) {
					// only scroll to a position if url does not contain a hash
					if (!urlContainsScrollableHash() && layData.misc_options_disable_ajax != 'on') {
						scrollWindowToTopInstantly()
					}
				} else {
					// projects_overlays.isSameStateOfLastOverview(currentState) can be true in this edge case:
					// we are on frontpage. we go to a project overlay, we go back. we go to about page, we go back to frontpage
					// in this case, projects_overlays.isSameStateOfLastOverview(currentState) is true
					// but then we still check, if the overlay is not open, this is just going from page to page, so we do want to scroll to the top here
					if (!projects_overlays.shouldCloseOverlayAlt()) {
						// so basically, this is called on pushstate from one page to the next if one page of them is the last saved overlay page
						if (!urlContainsScrollableHash() && layData.misc_options_disable_ajax != 'on') {
							scrollWindowToTopInstantly()
						}
					}
				}
			} else {
				// scroll normal page
				// only scroll to a position if url does not contain a hash
				if (!urlContainsScrollableHash() && layData.misc_options_disable_ajax != 'on') {
					scrollWindowToTopInstantly()
				}
			}
		}
	}

	window.laytheme.off('newpageshown', scrollToTop)
}

var urlContainsScrollableHash = function () {
	let containsScrollableHash = false
	if (window.location.href.indexOf('#') != -1) {
		containsScrollableHash = true
	}
	// except for when the anchor is just a thumbnailgrid filter
	if (window.location.href.indexOf('#category-') != -1) {
		containsScrollableHash = false
	}
	if (window.location.href.indexOf('#tag-') != -1) {
		containsScrollableHash = false
	}
	// using #product= etc. for the thumbnailgrid filter on elastique website :D
	if (window.location.href.indexOf('#product=') != -1) {
		containsScrollableHash = false
	}
	if (window.location.href.indexOf('#service=') != -1) {
		containsScrollableHash = false
	}
	if (window.location.href.indexOf('#client=') != -1) {
		containsScrollableHash = false
	}
	return containsScrollableHash
}

var currentSlidePos = 0
var bindFullscreenSlider = function () {
	// somehow window.magneticslidesPassedData is not defined yet here, so i just dont check for it
	// Handle fullpage.js events
	jQuery(window).on('fpAfterSlideLoad', function (e, obj) {
		// Only track position for main page, not for project overlays
		if (!projects_overlays.isOverlayDisplayBlock()) {
			currentSlidePos = obj.slideIndex
		}
	})
	jQuery(window).on('fpAfterLoad', function (e, obj) {
		// Only track position for main page, not for project overlays
		if (!projects_overlays.isOverlayDisplayBlock()) {
			currentSlidePos = obj.index
		}
	})
	// Handle CSS scroll snap events (same event names for compatibility)
}

var parent_selectors
var bindOnContentReady = function () {
	window.laytheme.on('content_ready', function (typeOfContentThatIsReady, $target, isProjectOverlayObj) {
		parent_selectors = undefined
		let parent = isProjectOverlayObj != undefined && isProjectOverlayObj.isProjectOverlay ? '#lay-project-overlay>.lay-content' : 'body>.lay-content'
		switch (typeOfContentThatIsReady) {
			case 'desktop':
				parent_selectors = [parent + ' #grid', parent + ' .cover-region-desktop', parent + ' #footer']
				break
			case 'phone':
				var footerHasPhoneCPL = jQuery(parent + ' #footer-custom-phone-grid').length != 0
				var hasCPL = jQuery(parent + ' #custom-phone-grid').length != 0

				if (footerHasPhoneCPL && hasCPL) {
					parent_selectors = [parent + ' #custom-phone-grid', parent + ' .cover-region-phone', parent + ' #footer-custom-phone-grid']
				} else if (!footerHasPhoneCPL && hasCPL) {
					parent_selectors = [parent + ' #custom-phone-grid', parent + ' .cover-region-phone', parent + ' #footer']
				} else if (footerHasPhoneCPL && !hasCPL) {
					parent_selectors = [parent + ' #grid', parent + ' .cover-region-desktop', parent + ' #footer-custom-phone-grid']
				} else if (!footerHasPhoneCPL && !hasCPL) {
					parent_selectors = [parent + ' #grid', parent + ' .cover-region-desktop', parent + ' #footer']
				}
				break
		}
	})
}

var i = function () {
	bindOnContentReady()
	jqueryMap.$body = jQuery('body')
	if (layData.misc_options_disable_ajax != 'on') {
		initHistory()
	}
	activateCurrentMenuPoint()
	bindAnchors()
	if (layData.misc_options_disable_ajax != 'on') {
		bindPopState()
	}
	setTimeout(function () {
		bindFullscreenSlider()
	}, 0)
}

// Update edit button URL when history changes
var updateEditButtonUrl = function (type, id) {
	var $editButton = jQuery('#lay-frontend-edit-button')
	if ($editButton.length === 0) {
		return
	}

	// Update data attributes
	$editButton.attr({
		'data-type': type,
		'data-id': id,
	})

	// Get edit URL via AJAX
	// Use ajax_url from layData or construct from wpapiroot
	var ajaxUrl = typeof layData !== 'undefined' && layData.ajax_url ? layData.ajax_url : typeof layData !== 'undefined' && layData.wpapiroot ? layData.wpapiroot.replace('/wp-json/', '/') + 'admin-ajax.php' : '/wp-admin/admin-ajax.php'

	jQuery.ajax({
		url: ajaxUrl,
		type: 'POST',
		data: {
			action: 'get_edit_url',
			type: type,
			id: id,
		},
		success: function (response) {
			if (response.success && response.data.edit_url) {
				jQuery('#lay-edit-button-link').attr('href', response.data.edit_url)
			}
		},
		error: function () {
			// Silently fail if AJAX doesn't work
		},
	})
}

var history_controller = {
	i: i,
	getState: getState,
	pushState: pushState,
	replaceState: replaceState,
	replaceCurrentStateHash: replaceCurrentStateHash,
	applyInitialHashAndReplace: applyInitialHashAndReplace,
	consumeInitialHashWithoutReplace: consumeInitialHashWithoutReplace,
	removeCurrentHash: removeCurrentHash,
	updateUrlSearchParam: updateUrlSearchParam,
	updateCurrentStateTitle: updateCurrentStateTitle,
	updateEditButtonUrl: updateEditButtonUrl,
	getScrollRestoreReady: getScrollRestoreReady,
	skipNextTransitionOut: function () {
		skipNextTransitionOut = true
	},
}

export default history_controller
